'use client';

import type { ReactNode} from 'react';
import { createContext, useContext, useState } from 'react';

import { CookieStorage } from '../../utils/cookie-storage';

export type FlagValuesType = Record<string, any>;

export type FlagsContextType = {
  flags: Record<string, boolean>;
  values: FlagValuesType;
  updateFlag: (key: string, value: boolean) => void;
  resetFlags: () => void;
};

const FlagContext = createContext<FlagsContextType>({
  flags: {},
  values: {},
  updateFlag: () => null,
  resetFlags: () => null,
});

const STORAGE_KEY = 'feature-flags';

export function FlagContextProvider<T>({
  initialFlags,
  children,
}: {
  initialFlags: FlagValuesType;
  children: ReactNode;
}) {
  const [cookies] = useState(new CookieStorage());

  const [flags, setFlags] = useState<Record<string, boolean>>(initialFlags);

  const updateFlag = (key: string, value: boolean) => {
    setFlags((prevFlags) => {
      cookies.setItem(STORAGE_KEY, JSON.stringify({ ...prevFlags, [key]: value }));

      return {
        ...prevFlags,
        [key]: value,
      };
    });
  };

  const resetFlags = () => {
    cookies.setItem(STORAGE_KEY, JSON.stringify(initialFlags));
    setFlags(initialFlags);
  };

  return (
    <FlagContext.Provider value={{ values: initialFlags, flags, updateFlag, resetFlags }}>
      {children}
    </FlagContext.Provider>
  );
}

export function useFlagContext() {
  return useContext(FlagContext);
}
